import { Box, Container, Stack, Text, useToast } from '@chakra-ui/react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Carousel from '../../carousel/Carousel';
import Colors from '../../../config/colors';
import BuyButton from '../../buyButton/buyButton';
import findRoute from '../../../utils/findRoute';
import EmptyAlert from '../../emptyAlert/EmptyAlert';
import {
    useCreateCryptomusCheckoutInvoiceMutation,
    useCreateTonkeeperCheckoutInvoiceMutation,
    useGetProductQuery,
} from '../../../redux/platimarket/platimarket.api';

import { AnimatePresence, motion } from 'framer-motion';

import '../../../styles/shine.css';
import Loader from '../../loader/Loader';
import ProductSection from './components/productSection/ProductSection';
import { useTranslation } from 'react-i18next';

import Parser from 'html-react-parser';
import { useEffect, useState } from 'react';
import { SendTransactionRequest, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { createTransaction, ICreateTransaction } from '../../../utils/tonkeeper';
import { useTelegram } from '../../../hooks/useTelegram';
import { useActions } from '../../../hooks/actions';
import { useAppSelector } from '../../../hooks/redux';
import { useLocaleType } from '../../../hooks/useLocaleType';
import { getFormatedPrice } from '../../../utils/price';
import SelectPaymentModal from './components/selectPayment/SelectPaymentModal';

const headerVariants = {
    hidden: {
        opacity: 0,
        y: 30,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            delay: 0.2,
        },
    },
};

const infoVariants = {
    hidden: {
        opacity: 0,
    },
    visible: {
        opacity: 1,
        transition: {
            delay: 0.4,
        },
    },
};

export default function Product() {
    const [t, i18n] = useTranslation();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, onClose } = useTelegram();

    const productId = Number(params.productId);
    const type = searchParams.get('type') ?? 'key';

    const { localeType } = useLocaleType(i18n);

    const {
        isLoading,
        isError,
        data: product,
    } = useGetProductQuery({
        id: productId,
        localeTypeName: localeType?.name,
        type,
    });

    const location = useLocation();
    const pathName = location.pathname;

    const findedRoute = findRoute(pathName);
    const emptyMessage = findedRoute?.emptyMessage;

    const currency = product?.localeType.currency;

    const [isOpen, setIsOpen] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    const productOptions = useAppSelector((state) => state.productOptions.options);
    const { setProductOptions } = useActions();

    const setIsOpenModal = (isOpen: boolean) => {
        if (productOptions.length) {
            navigate('/checkout', { state: { product } });
        } else {
            setIsOpen(isOpen);
        }
    };

    const [
        fetchCryptomusInvoice,
        {
            data: cryptomusInvoice,
            isSuccess: isSuccessCryptomusInvoice,
            isError: isErrorCryptomusInvoice,
            error: errorCryptomusInvoice,
        },
    ] = useCreateCryptomusCheckoutInvoiceMutation();

    const [
        fetchTonkeeperInvoice,
        {
            data: tonkeeperInvoice,
            isSuccess: isSuccessTonkeeperInvoice,
            isError: isErrorTonkeeperInvoice,
            error: errorTonkeeperInvoice,
        },
    ] = useCreateTonkeeperCheckoutInvoiceMutation();

    const createCryptomusInvoice = () => {
        if (product && currency && user?.username) {
            fetchCryptomusInvoice({
                productId,
                currency,
                gameActivationType: type,
                username: user.username,
            });
        }
    };

    const createTonkeeperInvoice = () => {
        if (product && currency && user?.username) {
            fetchTonkeeperInvoice({
                productId,
                currency,
                gameActivationType: type,
                username: user.username,
            });
        }
    };

    const wallet = useTonWallet();

    const [tonConnectUI, setOptions] = useTonConnectUI();

    const sendTransaction = async (data: ICreateTransaction) => {
        const transaction: SendTransactionRequest = createTransaction(data);

        tonConnectUI.sendTransaction(transaction);
    };

    const [isTonkeeper, setIsTonkeeper] = useState(false);

    useEffect(() => {
        if (isTonkeeper) {
            if (wallet) {
                createTonkeeperInvoice();
            } else {
                tonConnectUI.openSingleWalletModal('tonkeeper');
            }
        }
    }, [isTonkeeper]);

    useEffect(() => {
        if (isSuccessCryptomusInvoice && cryptomusInvoice) {
            window.open(cryptomusInvoice.paymentURL, '_self');
        }
    }, [isSuccessCryptomusInvoice]);

    tonConnectUI.onStatusChange((wallet) => {
        if (wallet && isTonkeeper) {
            createTonkeeperInvoice();
        }
    });

    useEffect(() => {
        if (isSuccessTonkeeperInvoice && tonkeeperInvoice) {
            sendTransaction({
                address: tonkeeperInvoice.payToAddress,
                amount: tonkeeperInvoice.amount,
                payload: tonkeeperInvoice.id,
            });
        }
    }, [isSuccessTonkeeperInvoice]);

    useEffect(() => {
        if (isErrorCryptomusInvoice || isErrorTonkeeperInvoice) {
            toast({
                description: t('product-is-out-of-stock'),
                status: 'error',
                duration: 10000,
            });
        }
    }, [isErrorCryptomusInvoice, isErrorTonkeeperInvoice]);

    useEffect(() => {
        if (product) {
            setProductOptions({ options: product.options });
        }
    }, [product]);

    return (
        <Container
            display="flex"
            flexDirection="column"
            gap="4"
            fontWeight="500"
            fontSize="16px"
            height="calc(100vh - 70px)">
            <SelectPaymentModal
                isOpen={isOpen}
                setIsOpenModal={setIsOpenModal}
                createCryptomusInvoice={createCryptomusInvoice}
                setIsTonkeeper={setIsTonkeeper}
            />
            {isLoading && <Loader position="absolute" margin="auto" inset="0" />}
            {!isLoading && !product && <EmptyAlert localeMessage={emptyMessage} />}
            {product && (
                <>
                    <Box
                        position="fixed"
                        display="flex"
                        alignItems="center"
                        bottom="6"
                        zIndex="6"
                        left="10px"
                        right="10px">
                        <BuyButton
                            name={`${t('buy')} ${getFormatedPrice(
                                product.price,
                                product?.localeType.currency,
                                product?.localeType.name,
                            )}`}
                            className="shine"
                            onClick={() => setIsOpenModal(true)}
                        />
                    </Box>
                    <Box width="100%" left="0" right="0" height="300px" position="absolute">
                        <Carousel images={product?.images ?? []} />
                    </Box>

                    <Box
                        position="absolute"
                        top="390px"
                        paddingBottom="100px"
                        left="10px"
                        right="10px">
                        <AnimatePresence>
                            <Box display="flex" flexDirection="column" gap="20px">
                                <motion.div
                                    variants={headerVariants}
                                    initial="hidden"
                                    animate="visible">
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        flexDirection="column"
                                        gap="11px">
                                        <Box display="flex" flexDirection="column" gap="1">
                                            <Text fontSize="16px" color="#fff">
                                                {product?.name}
                                            </Text>
                                        </Box>

                                        <Box fontSize="14px" display="flex" gap="10px">
                                            <Box color={Colors.darkGrey}>
                                                <Text>{t('developer')}</Text>
                                                <Text>{t('publisher')}</Text>
                                                <Text>{t('released')}</Text>
                                            </Box>
                                            <Box>
                                                <Text color={Colors.lightBlue}>
                                                    {product?.developers.join(', ')}
                                                </Text>
                                                <Text color={Colors.lightBlue}>
                                                    {product?.publishers.join(', ')}
                                                </Text>
                                                <Text color={Colors.lightGrey2}>
                                                    {product?.released}
                                                </Text>
                                            </Box>
                                        </Box>
                                    </Box>
                                </motion.div>
                                <motion.div
                                    variants={infoVariants}
                                    initial="hidden"
                                    animate="visible">
                                    <Stack spacing="20px">
                                        <Stack spacing="15px">
                                            <ProductSection
                                                title={t('categories')}
                                                names={product?.categories}
                                            />

                                            <ProductSection
                                                title={t('genres')}
                                                names={product?.genres}
                                            />
                                        </Stack>
                                        <Text color={Colors.lightGrey2} textAlign="justify">
                                            {Parser(product.description)}
                                        </Text>
                                    </Stack>
                                </motion.div>
                            </Box>
                        </AnimatePresence>
                    </Box>
                </>
            )}
        </Container>
    );
}
